import { AUTH_API_BASE as LOGIN_URL } from "#app/lib/api-bases";

// The auth service's zod `password()` schema enforces the same constant, so
// the forms' inline feedback can never disagree with the server.
export { PASSWORD_MIN_LENGTH } from "@salestrak/utilities";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginResponse {
  tokens: AuthTokens;
}

export interface RefreshResponse {
  tokens: AuthTokens;
}

/**
 * A rejected auth-service request. `code` is the envelope's optional
 * machine-readable discriminator (e.g. "EMAIL_NOT_VERIFIED") — the way for
 * callers to branch on the error kind; the message is display copy only.
 */
export class AuthRequestError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "AuthRequestError";
    if (code) this.code = code;
  }
}

/**
 * One fetch against the auth service. Serializes `body` as JSON, attaches the
 * bearer `token` when given, and unwraps the service's error envelope
 * (`{ error: { message, code? } }` or `{ message }`) into a thrown
 * AuthRequestError, falling back to `fallback (status)`.
 */
async function request<T>(
  path: string,
  fallback: string,
  init: { method?: string; body?: unknown; token?: string } = {}
): Promise<T> {
  const { method = "GET", body, token } = init;
  const response = await fetch(`${LOGIN_URL}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Read the body as text first: several endpoints answer 200 with no body at
  // all, so an empty one is success, not a parse failure.
  const raw = await response.text();
  let parsed: any = {};
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      // An error response's body is only read for its envelope, so an
      // unparseable one falls back to the status message. On a SUCCESSFUL
      // response the body IS the result: swallowing the failure would hand the
      // caller an empty object that reads as "succeeded with no data" — for
      // refreshTokens that means a null access token and a session that still
      // looks signed in with no bearer to send. Rethrowing keeps the caller's
      // isNetworkError() branch, which preserves the session instead.
      if (response.ok) throw err;
    }
  }

  if (!response.ok) {
    throw new AuthRequestError(
      parsed?.error?.message ?? parsed?.message ?? `${fallback} (${response.status})`,
      typeof parsed?.error?.code === "string" ? parsed.error.code : undefined
    );
  }

  return parsed as T;
}

/**
 * POST /oAuth/ - Create a new user account
 *
 * Mirrors: packages/oauth/services/api-service.js createAccount()
 */
export async function createAccount(user: {
  name: string;
  email: string;
  password: string;
}): Promise<void> {
  await request("/oAuth/", "Registration failed", { method: "POST", body: user });
}

/**
 * POST /oAuth/login/ - Authenticate with email + password
 *
 * Mirrors: packages/oauth/services/api-service.js loginToAccount()
 */
export function login(email: string, password: string): Promise<LoginResponse> {
  return request("/oAuth/login/", "Login failed", { method: "POST", body: { email, password } });
}

export interface GoogleLoginResponse {
  tokens: AuthTokens;
  /** True when the Google sign-in created a brand-new account. */
  created?: boolean;
}

/**
 * POST /oAuth/google - Authenticate with a Google popup-flow authorization
 * code. The server exchanges and verifies it, then signs in the linked user,
 * merges into an existing same-email account, or creates a new one.
 */
export function loginWithGoogle(code: string): Promise<GoogleLoginResponse> {
  return request("/oAuth/google", "Google sign-in failed", { method: "POST", body: { code } });
}

/**
 * PUT /oAuth/verify-email - Consume an emailed verification token and mark the
 * address verified. Returns NO session: the link proves the address is real,
 * not that this browser owns it, so the server never signs anyone in here (it
 * would be a login-CSRF). The caller routes to sign-in, like the reset flow.
 */
export async function verifyEmail(token: string): Promise<void> {
  await request("/oAuth/verify-email", "Verification failed", {
    method: "PUT",
    body: { token },
  });
}

/**
 * POST /oAuth/verify-email/resend - Re-send the verification link. The server
 * answers 200 whether or not the account exists or is already verified.
 */
export async function resendVerification(email: string): Promise<void> {
  await request("/oAuth/verify-email/resend", "Request failed", {
    method: "POST",
    body: { email },
  });
}

/**
 * POST /oAuth/reset-password - Request a password reset email
 *
 * Mirrors: packages/oauth/services/api-service.js requestPasswordReset()
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await request("/oAuth/reset-password", "Request failed", { method: "POST", body: { email } });
}

/**
 * PUT /oAuth/reset-password - Reset password with token
 *
 * Mirrors: packages/oauth/services/api-service.js resetPassword()
 */
export async function resetPassword(token: string, password: string): Promise<void> {
  await request("/oAuth/reset-password", "Reset failed", { method: "PUT", body: { token, password } });
}

export interface AcceptInviteResponse {
  tokens: AuthTokens;
  workspaceId: string;
}

/**
 * POST /oAuth/accept-invite - Consume an invite token and receive JWTs for
 * auto-login. For brand-new accounts, `name` is required and gets written to
 * the auth user alongside the password. For already-created accounts, `name`
 * is ignored and the password must match the existing credential.
 */
export function acceptInvite(
  token: string,
  password: string,
  name?: string
): Promise<AcceptInviteResponse> {
  return request("/oAuth/accept-invite", "Invitation failed", {
    method: "POST",
    body: name ? { token, password, name } : { token, password },
  });
}

export interface AcceptInviteGoogleResponse {
  tokens: AuthTokens;
  workspaceId: string;
  /** Resolved display name (existing account's, else the Google profile's). */
  name?: string;
}

/**
 * POST /oAuth/accept-invite/google - Consume an invite token, proving
 * identity with a Google popup-flow authorization code instead of a
 * password. The Google account must be the invited user's (linked identity
 * or verified matching email).
 */
export function acceptInviteWithGoogle(
  token: string,
  code: string
): Promise<AcceptInviteGoogleResponse> {
  return request("/oAuth/accept-invite/google", "Invitation failed", {
    method: "POST",
    body: { token, code },
  });
}

/**
 * GET /oAuth/auth-sources - The signed-in user's connected sign-in methods
 * ("password", "google", ...). The set is open — the auth REST API has no
 * codegen, and the server may know providers this build predates. Takes the
 * bearer token; callers hold it via useAuth().getAccessToken().
 */
export async function fetchAuthSources(accessToken: string): Promise<string[]> {
  const body = await request<{ sources?: string[] }>("/oAuth/auth-sources", "Request failed", {
    token: accessToken,
  });
  return body.sources ?? [];
}

/**
 * POST /oAuth/password/email - Email the signed-in user a link to set (or
 * reset) their password. Powers the security page's "Set password" action
 * for accounts without a password credential; the link runs the normal
 * reset-password flow.
 */
export async function requestPasswordSetupEmail(accessToken: string): Promise<void> {
  await request("/oAuth/password/email", "Request failed", {
    method: "POST",
    token: accessToken,
  });
}

/**
 * PUT /oAuth/password - Replace the signed-in user's password, proving
 * ownership with the current one.
 */
export async function changePassword(
  accessToken: string,
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await request("/oAuth/password", "Password change failed", {
    method: "PUT",
    body: { currentPassword, newPassword },
    token: accessToken,
  });
}

/**
 * GET /oAuth/tokens/:refreshToken - Refresh access token
 *
 * Mirrors: packages/oauth/services/api-service.js refreshTokens()
 */
export function refreshTokens(refreshToken: string): Promise<RefreshResponse> {
  return request(`/oAuth/tokens/${refreshToken}`, "Token refresh failed");
}
